import { useNavigate, useParams } from 'react-router-dom';
import { useWeddingVendor, useWeddingVendors } from '../../hooks/useWeddingVendors';
import { useVendorReviews } from '../../hooks/useWeddingReviews';
import { useFavorites } from '../../hooks/useFavorites';
import { useChat } from '../../context/ChatContext';
import VendorCard from '../../components/weddings/VendorCard';
import ReviewCard from '../../components/weddings/ReviewCard';
import ReviewModal from '../../components/weddings/ReviewModal';
import BookingCalendar from '../../components/weddings/BookingCalendar';
import { WeddingVendor } from '../../types';
import { ArrowLeft, Star, MapPin, Phone, Mail, Globe, Heart, Send, Utensils } from 'lucide-react';
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/tabs/tabs.js';
import '@material/web/tabs/primary-tab.js';
import '@material/web/dialog/dialog.js';
import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

export default function VendorProfilePage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: vendor, isLoading } = useWeddingVendor(id || '');
    const { isFavorite, toggleFavorite } = useFavorites();
    const [activeTab, setActiveTab] = useState<'overview' | 'packages' | 'gallery'>('overview');
    const [showInquiryModal, setShowInquiryModal] = useState(false);
    const [inquiryMessage, setInquiryMessage] = useState('');
    const [eventDate, setEventDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const { createChat, sendMessage, setActiveChatId } = useChat();

    const handleSendInquiry = async () => {
        if (!vendor) return;
        setSubmitting(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                toast.error('Please sign in to send an inquiry');
                return;
            }
            // Start Chat or get existing one first
            const chatId = await createChat(vendor.owner_id);

            // Create Inquiry linked to Chat
            const { error } = await supabase.from('wedding_inquiries').insert({
                user_id: session.user.id,
                vendor_id: vendor.id,
                event_date: eventDate || null,
                message: inquiryMessage,
                chat_id: chatId // Link to chat
            });
            if (error) throw error;

            // Send initial message
            await sendMessage(chatId, `Expressed interest for event on ${eventDate || 'undecided date'}.\n\nMessage: ${inquiryMessage}`);

            setActiveChatId(chatId);

            toast.success('Inquiry sent successfully! Chat started.');
            setShowInquiryModal(false);
            setInquiryMessage('');
            setEventDate('');
        } catch (err: any) {
            toast.error(err.message || 'Failed to send inquiry');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRequestTasting = () => {
        setInquiryMessage("Hi, I'm interested in scheduling a food tasting session for my upcoming event. Could you please let me know your availability and any associated costs?");
        setShowInquiryModal(true);
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-500">Loading vendor...</p>
            </div>
        );
    }

    if (!vendor) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <p className="text-gray-500">Vendor not found</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Navbar / Actions */}
            <div className="max-w-7xl mx-auto px-4 pt-6 pb-4 flex justify-between items-center bg-white dark:bg-gray-900 sticky top-0 z-50">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                    <ArrowLeft size={20} />
                    Back
                </button>
                <div className="flex items-center gap-3">
                    {/* Share Button Placeholder if needed */}
                    {id && (
                        <button
                            onClick={() => toggleFavorite(id)}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                            <Heart
                                size={20}
                                className={isFavorite(id) ? 'text-rose-600 fill-rose-600' : 'text-gray-600 dark:text-gray-400'}
                            />
                            <span className={`font-medium underline ${isFavorite(id) ? 'text-rose-600' : 'text-gray-900 dark:text-white'}`}>
                                {isFavorite(id) ? 'Saved' : 'Save'}
                            </span>
                        </button>
                    )}
                </div>
            </div>

            {/* Gallery Grid (Airbnb Style) */}
            <div className="max-w-7xl mx-auto px-4 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-4 grid-rows-2 gap-2 h-[40vh] md:h-[50vh] rounded-2xl overflow-hidden">
                    {/* Primary Image */}
                    <div className="md:col-span-2 md:row-span-2 relative">
                        <img
                            src={vendor.images?.[0] || 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800'}
                            alt={vendor.name}
                            className="w-full h-full object-cover hover:opacity-95 transition-opacity cursor-pointer"
                            onClick={() => setActiveTab('gallery')}
                        />
                    </div>
                    {/* Secondary Images */}
                    <div className="hidden md:block">
                        <img
                            src={vendor.images?.[1] || vendor.packages?.[0]?.images?.[0] || 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400'}
                            alt="Gallery 2"
                            className="w-full h-full object-cover hover:opacity-95 transition-opacity cursor-pointer"
                            onClick={() => setActiveTab('gallery')}
                        />
                    </div>
                    <div className="hidden md:block">
                        <img
                            src={vendor.images?.[2] || vendor.packages?.[0]?.images?.[1] || 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400'}
                            alt="Gallery 3"
                            className="w-full h-full object-cover hover:opacity-95 transition-opacity cursor-pointer"
                            onClick={() => setActiveTab('gallery')}
                        />
                    </div>
                    <div className="hidden md:block">
                        <img
                            src={vendor.images?.[3] || vendor.packages?.[1]?.images?.[0] || 'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=400'}
                            alt="Gallery 4"
                            className="w-full h-full object-cover hover:opacity-95 transition-opacity cursor-pointer"
                            onClick={() => setActiveTab('gallery')}
                        />
                    </div>
                    <div className="hidden md:block relative">
                        <img
                            src={vendor.images?.[4] || vendor.packages?.[1]?.images?.[1] || 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=400'}
                            alt="Gallery 5"
                            className="w-full h-full object-cover hover:opacity-95 transition-opacity cursor-pointer"
                            onClick={() => setActiveTab('gallery')}
                        />
                        <button
                            onClick={() => {
                                setActiveTab('gallery');
                                document.getElementById('vendor-tabs')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="absolute bottom-4 right-4 bg-white dark:bg-gray-800 px-3 py-1.5 rounded-lg text-sm font-semibold shadow-md border border-gray-200 dark:border-gray-700 hover:scale-105 transition-transform"
                        >
                            Show all photos
                        </button>
                    </div>
                </div>
            </div>

            {/* Vendor Header Info */}
            <div className="max-w-7xl mx-auto px-4 mb-8">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">{vendor.name}</h1>
                        <div className="flex flex-wrap items-center gap-4 text-gray-600 dark:text-gray-400">
                            <div className="flex items-center gap-1">
                                <Star size={18} className="text-rose-600 fill-rose-600" />
                                <span className="font-semibold text-gray-900 dark:text-white">{vendor.rating ? vendor.rating.toFixed(1) : 'New'}</span>
                                <span className="underline cursor-pointer">({vendor.review_count || 0} reviews)</span>
                            </div>
                            <span className="hidden md:inline">•</span>
                            <div className="flex items-center gap-1 text-sm font-medium">
                                <span className="bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded text-gray-900 dark:text-white">{vendor.category?.name}</span>
                            </div>
                            <span className="hidden md:inline">•</span>
                            <div className="flex items-center gap-1 underline cursor-pointer hover:text-rose-600">
                                <MapPin size={16} />
                                {vendor.city || 'Location available on request'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-8 border-b border-gray-200 dark:border-gray-700" />
            </div>

            {/* Content */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content */}
                    <div className="flex-1">
                        {/* Tabs */}
                        <div id="vendor-tabs" className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 mb-6 overflow-hidden">
                            <div className="flex border-b border-gray-200 dark:border-gray-700">
                                {(['overview', 'packages', 'gallery'] as const).map((tab) => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`flex-1 py-4 text-sm font-medium capitalize transition-colors ${activeTab === tab
                                            ? 'text-rose-600 border-b-2 border-rose-600'
                                            : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                                            }`}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>

                            <div className="p-6">
                                {activeTab === 'overview' && (
                                    <div>
                                        <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-3">About</h3>
                                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                            {vendor.description || 'No description provided.'}
                                        </p>

                                        {vendor.address && (
                                            <div className="mt-6">
                                                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Address</h4>
                                                <p className="text-gray-600 dark:text-gray-400">{vendor.address}</p>
                                            </div>
                                        )}

                                        {/* Category-specific details */}
                                        {vendor.details && Object.keys(vendor.details).length > 0 && (
                                            <div className="mt-6">
                                                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Details</h4>
                                                <div className="grid grid-cols-2 gap-4">
                                                    {Object.entries(vendor.details).map(([key, value]) => (
                                                        <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                                                            <span className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                                                                {key.replace(/_/g, ' ')}
                                                            </span>
                                                            <p className="font-medium text-gray-900 dark:text-white">
                                                                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                                                            </p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'packages' && (
                                    <div>
                                        {vendor.packages && vendor.packages.length > 0 ? (
                                            <div className="space-y-4">
                                                {vendor.packages.map((pkg: any) => (
                                                    <div
                                                        key={pkg.id}
                                                        className="border border-gray-200 dark:border-gray-700 rounded-xl p-5"
                                                    >
                                                        <div className="flex justify-between items-start mb-2">
                                                            <h4 className="font-semibold text-lg text-gray-900 dark:text-white">
                                                                {pkg.name}
                                                            </h4>
                                                            <span className="text-rose-600 font-bold text-lg">
                                                                ₹{pkg.price?.toLocaleString()}
                                                            </span>
                                                        </div>
                                                        <p className="text-gray-600 dark:text-gray-400">{pkg.description}</p>
                                                        {pkg.images && pkg.images.length > 0 && (
                                                            <div className="flex gap-2 mt-4 overflow-x-auto pb-2 scrollbar-hide">
                                                                {pkg.images.map((img: string, idx: number) => (
                                                                    <img
                                                                        key={idx}
                                                                        src={img}
                                                                        alt={`${pkg.name} ${idx + 1}`}
                                                                        className="w-32 h-32 object-cover rounded-lg flex-shrink-0 border border-gray-200 dark:border-gray-700"
                                                                    />
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-center py-8">No packages listed yet.</p>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'gallery' && (
                                    <div>
                                        {vendor.images && vendor.images.length > 0 ? (
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                {vendor.images.map((img, idx) => (
                                                    <img
                                                        key={idx}
                                                        src={img}
                                                        alt={`Gallery ${idx + 1}`}
                                                        className="w-full h-48 object-cover rounded-lg"
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-gray-500 text-center py-8">No gallery images yet.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <aside className="lg:w-80 flex-shrink-0">
                        {/* Booking Calendar for Venues/Guest Houses */}
                        {['venues', 'venue', 'guest-houses', 'guest-house'].includes(vendor.category?.slug || '') ? (
                            <BookingCalendar vendor={vendor} />
                        ) : (
                            /* Standard Contact Card for other vendors */
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sticky top-24">
                                <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">Contact</h3>

                                <div className="space-y-3 mb-6">
                                    {vendor.contact_phone && (
                                        <a
                                            href={`tel:${vendor.contact_phone}`}
                                            className="flex items-center gap-3 text-gray-600 dark:text-gray-400 hover:text-rose-600 transition-colors"
                                        >
                                            <Phone size={18} />
                                            {vendor.contact_phone}
                                        </a>
                                    )}
                                    {vendor.contact_email && (
                                        <a
                                            href={`mailto:${vendor.contact_email}`}
                                            className="flex items-center gap-3 text-gray-600 dark:text-gray-400 hover:text-rose-600 transition-colors"
                                        >
                                            <Mail size={18} />
                                            {vendor.contact_email}
                                        </a>
                                    )}
                                    {vendor.website && (
                                        <a
                                            href={vendor.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 text-gray-600 dark:text-gray-400 hover:text-rose-600 transition-colors"
                                        >
                                            <Globe size={18} />
                                            Website
                                        </a>
                                    )}
                                </div>

                                <button
                                    onClick={() => setShowInquiryModal(true)}
                                    className="w-full py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 mb-3"
                                >
                                    <Send size={18} />
                                    Request Quote
                                </button>

                                {/* Tasting Session Button (Catering Only) */}
                                {vendor.details?.tasting_available && (
                                    <button
                                        onClick={handleRequestTasting}
                                        className="w-full py-3 bg-white hover:bg-gray-50 text-rose-600 border border-rose-200 dark:border-rose-900/30 dark:bg-gray-800 dark:hover:bg-gray-700 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 mb-3"
                                    >
                                        <Utensils size={18} />
                                        Request Tasting
                                    </button>
                                )}

                                {/* Add to Favorites */}
                                {id && (
                                    <button
                                        onClick={() => toggleFavorite(id)}
                                        className={`w-full py-3 font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 border ${isFavorite(id)
                                            ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 border-rose-600'
                                            : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-rose-400'
                                            }`}
                                    >
                                        <Heart size={18} className={isFavorite(id) ? 'fill-rose-600' : ''} />
                                        {isFavorite(id) ? 'Saved to Favorites' : 'Add to Favorites'}
                                    </button>
                                )}
                            </div>
                        )}
                    </aside>
                </div>
            </div>

            {/* Similar Vendors */}
            <SimilarVendors currentVendor={vendor} />

            {/* Reviews Section */}
            <VendorReviews vendorId={vendor.id} />

            {/* Inquiry Modal */}
            {showInquiryModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Send Inquiry</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Event Date (Optional)
                                </label>
                                <input
                                    type="date"
                                    value={eventDate}
                                    onChange={(e) => setEventDate(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Message
                                </label>
                                <textarea
                                    value={inquiryMessage}
                                    onChange={(e) => setInquiryMessage(e.target.value)}
                                    rows={4}
                                    placeholder="Tell the vendor about your event..."
                                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowInquiryModal(false)}
                                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSendInquiry}
                                disabled={submitting || !inquiryMessage.trim()}
                                className="flex-1 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
                            >
                                {submitting ? 'Sending...' : 'Send'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Mobile Sticky Action Bar */}
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 lg:hidden z-50 flex items-center justify-between shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                <div className="flex flex-col">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Interested?
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        Send a message to check availability
                    </span>
                </div>
                <button
                    onClick={() => setShowInquiryModal(true)}
                    className="bg-rose-600 hover:bg-rose-700 text-white font-semibold py-2.5 px-6 rounded-lg transition-colors flex items-center gap-2"
                >
                    <Send size={18} />
                    Request Quote
                </button>
            </div>
        </div>
    );
}

function SimilarVendors({ currentVendor }: { currentVendor: WeddingVendor }) {
    // Recommend vendors in the same category AND nearby (within 50km)
    // If vendor has no location, defaults to just category match
    const { data: allVendors = [] } = useWeddingVendors({
        categorySlug: currentVendor.category?.slug,
        lat: currentVendor.latitude || undefined,
        lon: currentVendor.longitude || undefined,
        radius: 50
    });

    // Filter out current vendor and limit to 3
    const similar = allVendors
        .filter(v => v.id !== currentVendor.id)
        .slice(0, 3);

    if (similar.length === 0) return null;

    return (
        <section className="bg-white dark:bg-gray-800 py-12 border-t border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Similar Vendors</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {similar.map(vendor => (
                        <div key={vendor.id} className="h-full">
                            <VendorCard vendor={vendor} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function VendorReviews({ vendorId }: { vendorId: string }) {
    const { data: reviews = [] } = useVendorReviews(vendorId);
    const [showModal, setShowModal] = useState(false);

    const handleWriteReview = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
            toast.error('Please sign in to write a review');
            return;
        }
        setShowModal(true);
    };

    return (
        <section id="reviews" className="bg-gray-50 dark:bg-gray-900 py-12 border-t border-gray-200 dark:border-gray-700">
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            Reviews
                            <span className="text-gray-500 text-lg font-normal">({reviews.length})</span>
                        </h2>
                    </div>
                    <button
                        onClick={handleWriteReview}
                        className="bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                        Write a Review
                    </button>
                </div>

                {reviews.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {reviews.map((review: any) => (
                            <ReviewCard key={review.id} review={review} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
                        <p className="text-gray-500 mb-4">No reviews yet. Be the first to share your experience!</p>
                        <button
                            onClick={handleWriteReview}
                            className="text-rose-600 font-medium hover:underline"
                        >
                            Write a Review
                        </button>
                    </div>
                )}

                {showModal && (
                    <ReviewModal
                        vendorId={vendorId}
                        onClose={() => setShowModal(false)}
                    />
                )}
            </div>
        </section>
    );
}

